import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"
import { formatCurrency } from "./currency.mjs"

const t = (key, options) => I18n.t(`erp.sales.js.marking_form.${key}`, options)

const MILLIMETRES = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 })
const wizardT = (key, options) => I18n.t(`erp.sales.js.marking_wizard.${key}`, options)

const COMPATIBILITY_CLASSES = {
  allowed: "bg-erp-st-won-bg text-erp-st-won-fg",
  approval_required: "bg-erp-st-pending-bg text-erp-st-pending-fg",
  incompatible: "bg-[#FBE3DE] text-[#8E2212]",
  unspecified: "bg-[#F2EEE3] text-erp-st-draft-fg"
}

// One marking card (_marking_card): its technique and zone choices, the pieces of each textile size it applies to,
// the tariff lookup that follows from the resulting total quantity, and the card summary kept in step with them.
// The 4-step wizard (erp--marking-wizard) shows these fields; this controller gives them their meaning.
// Connects to data-controller="erp--marking-form"
export default class extends Controller {
  static values = {
    lookupUrl: String,
    dimensionsUrl: String,
    customerType: String,
    structuredPricing: Boolean,
    // Sofidial's rules (R1 to R3): techniques without a format, those whose format is the pricing choice, and the
    // colours a screen print may choose (ERP::EstimateMarkingsHelper#marking_format_rules)
    formatlessTechniques: Array,
    formatPricedTechniques: Array,
    screenPrintMaxColours: Number
  }

  static targets = [
    "technique", "techniqueChoice", "subTechnique", "subTechniqueField", "pricingChoiceField", "position", "zoneChoice", "customZone",
    "customZoneHint", "dimension", "width", "height", "formatField", "format", "formatHint", "textileType", "compatibilityOverride", "compatibilityReason",
    "manualPriceReason", "quantity", "unitPrice", "unitPriceDisplay", "unitPriceCurrency", "priceOverride",
    "priceFeedback", "linkRow", "linkQuantity", "linkDestroy",
    "allocationRow", "allocationQuantity", "allocationDestroy", "allocationItemCheckbox", "allocationMasterCheckbox",
    "allocationTotal", "compatibilityRow", "extraRow", "extraCheckbox", "extraDestroy", "extraEmpty",
    "techniqueBadge", "techniqueIcon", "positionBadge", "customZoneTag", "underbaseTag", "dimensionsSummary",
    "personalizedPieces", "personalizedPiecesMax", "personalizedSummary",
    "linkSummary", "quantitySummary", "totalSummary",
    "discountPercentageField", "discountAmountField", "vatSummary"
  ]

  connect() {
    this._dimensionsAbortController = null
    this.#syncSubTechniqueField()
    this.#syncFormatField()
    this.compatibilityOverrideChanged()
    this.#toggleManualPriceReason()
    this.#syncUnitPricePresentation()
    this.#syncDiscountFields()
    this.#syncVatSummary()
    this.#syncExtraAvailability()
    this.#refreshBadges()
    this.#refreshDimensionsSummary()
    this.#syncFormats()
    this.#refreshCompatibility()
    this.#syncAllocationState()
    this.#syncQuantity()
    if (this.hasTechniqueTarget && this.techniqueTarget.value) {
      if (this.structuredPricingValue) this.loadDimensions()
      else this.loadDimensions().then(() => this.lookupPrice())
    }
  }

  disconnect() {
    this._dimensionsAbortController?.abort()
  }

  // ── Pieces of each textile size ──────────────────────────────

  quantityChanged() {
    this.#syncQuantity()
    this.lookupPrice()
  }

  // A size quantity stays between 0 and the pieces of the size; 0 leaves the size out of the marking
  allocationQuantityChanged(event) {
    const input = event.target
    const maximum = parseInt(input.max)
    let quantity = Math.max(0, parseInt(input.value) || 0)
    if (Number.isFinite(maximum) && quantity > maximum) quantity = maximum
    if (input.value !== "" && String(quantity) !== input.value) input.value = quantity
    this.#setAllocation(input.closest("[data-erp--marking-form-target='allocationRow']"), quantity)
    this.#allocationsChanged()
  }

  toggleAllocationItem(event) {
    const section = event.target.closest("[data-allocation-item]")
    this.#fillAllocations(this.#visibleAllocationRows(section), event.target.checked)
  }

  toggleAllAllocations(event) {
    this.#fillAllocations(this.#visibleAllocationRows(), event.target.checked)
  }

  // Every size of every textile, all pieces: a new marking starts from there
  selectAllAllocations() {
    this.#fillAllocations(this.#visibleAllocationRows(), true)
  }

  #fillAllocations(rows, selected) {
    rows.forEach(row => {
      const input = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
      const quantity = selected ? parseInt(input?.max) || 0 : 0
      if (input) input.value = quantity
      this.#setAllocation(row, quantity)
    })
    this.#allocationsChanged()
  }

  #setAllocation(row, quantity) {
    if (!row) return

    const destroy = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")
    if (destroy) destroy.value = quantity > 0 ? "0" : "1"
    row.querySelector("[data-erp--marking-form-target='allocationQuantity']")?.toggleAttribute("data-filled", quantity > 0)
  }

  #allocationsChanged() {
    this.#syncAllocationState()
    this.#syncQuantity()
    this.lookupPrice()
    this.#notifyFormMutation(this.allocationDestroyTargets[0])
  }

  // Refreshes the "8 / 20 pcs" counts and the textile and "all textiles" boxes, checked or partly checked
  allocationsResynced() {
    this.#syncAllocationState()
    this.#syncQuantity()
  }

  #syncAllocationState() {
    let marked = 0
    let total = 0
    this.element.querySelectorAll("[data-allocation-item]").forEach(section => {
      const counts = this.#allocationCounts(this.#visibleAllocationRows(section))
      marked += counts.marked
      total += counts.total
      section.querySelector("[data-allocation-item-pieces]")?.replaceChildren(this.#piecesLabel(counts))
      this.#syncCheckbox(section.querySelector("[data-erp--marking-form-target='allocationItemCheckbox']"), counts)
    })
    if (this.hasAllocationTotalTarget) this.allocationTotalTarget.textContent = this.#piecesLabel({ marked, total })
    if (this.hasAllocationMasterCheckboxTarget) this.#syncCheckbox(this.allocationMasterCheckboxTarget, { marked, total })
  }

  #allocationCounts(rows) {
    return rows.reduce((counts, row) => {
      const input = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
      const destroyed = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value === "1"
      counts.marked += destroyed ? 0 : parseInt(input?.value) || 0
      counts.total += parseInt(input?.max) || 0
      return counts
    }, { marked: 0, total: 0 })
  }

  #syncCheckbox(checkbox, { marked, total }) {
    if (!checkbox) return

    checkbox.checked = total > 0 && marked >= total
    checkbox.indeterminate = marked > 0 && marked < total
    checkbox.disabled = total === 0
  }

  #piecesLabel({ marked, total }) {
    return t("pieces_of", { marked, total })
  }

  #visibleAllocationRows(scope = this.element) {
    return [ ...scope.querySelectorAll("[data-erp--marking-form-target='allocationRow']") ]
      .filter(row => !row.classList.contains("hidden"))
  }

  #notifyFormMutation(field) {
    field?.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // ── Technique and dimensions ─────────────────────────────────

  canonicalTechniqueChanged() {
    if (!this.hasTechniqueTarget) return

    this.techniqueTarget.value = this.#checkedTechnique()?.dataset.code || ""
    this.techniqueChanged()
  }

  async techniqueChanged() {
    if (this.hasSubTechniqueTarget) this.subTechniqueTarget.value = ""
    this.#syncSubTechniqueField()

    this.#refreshBadges()
    this.#syncExtraAvailability()
    this.#syncFormatField()
    this.#formatForTechnique()
    this.#syncFormats()
    this.#refreshCompatibility()
    await this.loadDimensions({ preserveSelection: false })
    this.#syncFormatDimension()
    this.lookupPrice()
  }

  async subTechniqueChanged() {
    await this.loadDimensions({ preserveSelection: false })
    this.#syncFormatDimension()
    this.lookupPrice()
  }

  #syncSubTechniqueField() {
    if (this.hasPricingChoiceFieldTarget && this.hasTechniqueTarget) {
      // The DTF format, chosen at step 2, is its pricing choice
      this.pricingChoiceFieldTarget.hidden = !this.techniqueTarget.value || this.#formatPriced()
      this.#syncColourCount()
    }
    if (!this.hasSubTechniqueTarget || !this.hasSubTechniqueFieldTarget) return

    const isDtf = this.hasTechniqueTarget && this.techniqueTarget.value === "dtf"
    this.subTechniqueFieldTarget.classList.toggle("hidden", !isDtf)
    if (!isDtf) this.subTechniqueTarget.value = ""
  }

  positionChanged() {
    this.#refreshBadges()
    this.#syncFormats()
    this.#refreshCompatibility()
    this.recalculate()
  }

  // A zone without dimensions yet takes the format advised for it
  zoneChanged() {
    if (!this.hasPositionTarget) return

    const zone = this.#checkedZone()
    if (zone && this.hasCustomZoneTarget) this.customZoneTarget.value = ""
    this.positionTarget.value = zone?.dataset.label || ""
    this.#applyAdvisedFormat(zone)
    this.positionChanged()
  }

  #applyAdvisedFormat(zone) {
    if (!zone || this.#hasDimensions() || this.#formatless()) return

    const advised = this.formatTargets.find(format => format.dataset.format === zone.dataset.advisedFormat)
    if (!advised) return

    this.#applyFormat(advised)
    this.#syncFormatDimension()
  }

  // A custom zone replaces the canonical one: it needs a manager's approval before sending
  customZoneChanged() {
    if (!this.hasCustomZoneTarget || !this.hasPositionTarget) return

    const customName = this.customZoneTarget.value.trim()
    if (customName) this.zoneChoiceTargets.forEach(choice => { choice.checked = false })
    this.positionTarget.value = customName
    this.positionChanged()
  }

  compatibilityOverrideChanged() {
    if (!this.hasCompatibilityReasonTarget) return

    const overridden = this.hasCompatibilityOverrideTarget && this.compatibilityOverrideTarget.checked
    this.compatibilityReasonTarget.classList.toggle("hidden", !overridden)
  }

  compatibilityChanged() {
    this.#refreshCompatibility()
  }

  // ── Formats (A6, A4, A3: R3) ─────────────────────────────────

  // A format fills the width and height, and the pricing choice of DTF
  formatChosen(event) {
    const format = event.currentTarget
    this.#applyFormat(format)
    this.#syncFormatDimension()
    this.#syncFormats()
    this.#refreshCompatibility()
    this.#notifyFormMutation(this.heightTarget)
    format.focus()
  }

  #applyFormat(format) {
    if (!this.hasWidthTarget || !this.hasHeightTarget) return

    this.widthTarget.value = format.dataset.width
    this.heightTarget.value = format.dataset.height
    this.#refreshDimensionsSummary()
  }

  // The format matching the dimensions, the one advised for the zone, the hint
  #syncFormats() {
    if (!this.hasFormatTarget) return

    const advisedFormat = this.#checkedZone()?.dataset.advisedFormat
    this.formatTargets.forEach(format => this.#syncFormat(format, advisedFormat))
    if (this.hasFormatHintTarget) this.formatHintTarget.textContent = this.#formatHint()
  }

  #syncFormat(format, advisedFormat) {
    format.setAttribute("aria-checked", String(this.#formatMatches(format)))
    format.querySelector("[data-format-advised]")?.classList.toggle("hidden", format.dataset.format !== advisedFormat)
  }

  // Screen printing and embroidery take the zone alone
  #syncFormatField() {
    if (this.hasFormatFieldTarget) this.formatFieldTarget.hidden = this.#formatless()
  }

  // A technique without a format drops the size of the previous one; one with a format takes the advised one
  #formatForTechnique() {
    if (!this.#formatless()) return this.#applyAdvisedFormat(this.#checkedZone())
    if (!this.#hasDimensions()) return

    this.widthTarget.value = ""
    this.heightTarget.value = ""
    this.#refreshDimensionsSummary()
  }

  // DTF prices its format (`dimension_1`): the format chosen at step 2 is written there
  #syncFormatDimension() {
    if (!this.#formatPriced() || !this.hasDimensionTarget) return

    const format = this.formatTargets.find(candidate => this.#formatMatches(candidate))?.dataset.format || ""
    const select = this.dimensionTarget
    if (select.value === format) return
    if (format && ![ ...select.options ].some(option => option.value === format)) select.appendChild(new Option(format, format))
    select.value = format
    select.dispatchEvent(new Event("change", { bubbles: true }))
  }

  #techniqueCode() {
    return this.hasTechniqueTarget ? this.techniqueTarget.value : ""
  }

  #formatless() {
    return this.formatlessTechniquesValue.includes(this.#techniqueCode())
  }

  #formatPriced() {
    return this.formatPricedTechniquesValue.includes(this.#techniqueCode())
  }

  #formatHint() {
    const zone = this.#checkedZone()
    if (zone?.dataset.advisedFormat) {
      return wizardT("format_advised", { zone: zone.dataset.wizardLabel || zone.dataset.label, format: zone.dataset.advisedFormat })
    }
    const custom = this.hasCustomZoneTarget && this.customZoneTarget.value.trim()
    return zone || custom ? "" : wizardT("zone_first")
  }

  #hasDimensions() {
    return this.hasWidthTarget && this.hasHeightTarget &&
      (this.#dimensionValue(this.widthTarget.value) > 0 || this.#dimensionValue(this.heightTarget.value) > 0)
  }

  #formatMatches(format) {
    if (!this.hasWidthTarget || !this.hasHeightTarget) return false

    return this.#dimensionValue(this.widthTarget.value) === Number(format.dataset.width) &&
      this.#dimensionValue(this.heightTarget.value) === Number(format.dataset.height)
  }

  // Screen printing asks for the number of colours of the logo, as a grid of numbers (`.tech__colors`)
  #syncColourCount() {
    const field = this.pricingChoiceFieldTarget
    const colourCount = this.techniqueTarget.value === "screen_print"
    field.dataset.colourCount = String(colourCount)
    const choices = field.querySelector("[data-erp--select-chips-target='choices']")
    if (choices) choices.setAttribute("aria-label", colourCount ? choices.dataset.colourLabel : choices.dataset.pricingLabel)
  }

  toggleExtra(event) {
    const row = event.target.closest("[data-erp--marking-form-target='extraRow']")
    const destroy = row?.querySelector("[data-erp--marking-form-target='extraDestroy']")
    if (!destroy) return

    destroy.value = event.target.checked ? "0" : "1"
    this.#syncExtraAvailability()
    // The fee is server-owned, including for a manually priced marking. Never
    // preview the old fee total while the changed selection is being saved.
    if (this.structuredPricingValue) this.#markPricingStale()
    else this.lookupPrice()
    this.#notifyFormMutation(destroy)
  }

  overrideChanged() {
    if (!this.hasUnitPriceTarget) return

    const overridden = this.#overridden()
    this.unitPriceTarget.readOnly = !overridden
    this.#setInputMode(this.unitPriceTarget, overridden)
    this.#syncUnitPricePresentation()
    this.#toggleManualPriceReason()

    if (overridden) this.recalculate()
    else this.lookupPrice()
  }

  recalculate() {
    this.#syncVatSummary()
    if (this.structuredPricingValue && !this.#overridden()) return this.#markPricingStale()

    this.#refreshSummary()
    this.dispatch("changed")
  }

  calculatePrice(event) {
    event.preventDefault()
    this.element.closest("form")?.requestSubmit()
  }

  discountTypeChanged() {
    this.#syncDiscountFields()
    this.recalculate()
  }

  async customerContextChanged(customerType = this.#customerType()) {
    this.#remapExtrasForCustomerType(customerType)
    this.#syncExtraAvailability()
    if (this.structuredPricingValue) this.#markPricingStale()
    await this.loadDimensions({ preserveSelection: true })
    if (!this.structuredPricingValue) await this.lookupPrice()
  }

  customerTypeValueChanged() {
    if (!this.element.isConnected) return

    this.#syncExtraAvailability()
    this.loadDimensions({ preserveSelection: true })
  }

  async loadDimensions({ preserveSelection = true } = {}) {
    if (!this.hasDimensionTarget || !this.hasTechniqueTarget) return

    const select = this.dimensionTarget
    const previous = select.value
    this._dimensionsAbortController?.abort()

    const technique = this.techniqueTarget.value
    if (!technique || !this.dimensionsUrlValue) {
      select.replaceChildren(new Option("—", ""))
      return
    }
    // On initial load, keep the persisted option in the form until the lookup has
    // succeeded. Otherwise a slow/offline request could make an immediate Save erase it.
    if (!preserveSelection) select.replaceChildren(new Option("—", ""))

    const params = new URLSearchParams({ technique, customer_type: this.#customerType() })
    if (this.hasSubTechniqueTarget && this.subTechniqueTarget.value) {
      params.set("sub_technique", this.subTechniqueTarget.value)
    }
    const request = new AbortController()
    this._dimensionsAbortController = request

    try {
      const response = await fetch(`${this.dimensionsUrlValue}?${params}`, {
        headers: { Accept: "application/json" },
        signal: request.signal
      })
      if (request !== this._dimensionsAbortController) return
      if (!response.ok) return

      const data = await response.json()
      select.replaceChildren(new Option("—", ""))
      const dimensions = this.#choosableDimensions(technique, data.dimensions)
      dimensions.forEach(dimension => select.appendChild(new Option(dimension, dimension)))
      // A choice no longer offered (an older 8-colour screen print) stays selected, flagged unavailable
      if (preserveSelection && previous) {
        if (!dimensions.includes(previous)) select.appendChild(new Option(`${previous} — ${t("unavailable")}`, previous))
        select.value = previous
      }
    } catch (error) {
      if (error.name !== "AbortError") console.warn("[marking-form] Dimensions loading failed:", error)
    }
  }

  // A screen print chooses 1 to 7 colours: the last column of its grid is 7 colours with the underbase (R1)
  #choosableDimensions(technique, dimensions) {
    if (technique !== "screen_print" || !this.screenPrintMaxColoursValue) return dimensions

    return dimensions.filter(dimension => !/^\d+$/.test(dimension) || Number(dimension) <= this.screenPrintMaxColoursValue)
  }

  // The tariff bracket follows the *total* quantity marked across every attached textile.
  async lookupPrice() {
    if (!this.hasTechniqueTarget || this.#overridden()) return this.recalculate()
    if (this.structuredPricingValue) return this.recalculate()

    const technique = this.techniqueTarget.value
    if (!technique) {
      if (this.hasUnitPriceTarget) this.unitPriceTarget.value = "0.00"
      this.#setFeedback(null)
      return this.recalculate()
    }

    const params = new URLSearchParams({
      technique,
      customer_type: this.#customerType(),
      quantity: this.hasQuantityTarget ? this.quantityTarget.value : "0"
    })
    if (this.hasSubTechniqueTarget && this.subTechniqueTarget.value) params.set("sub_technique", this.subTechniqueTarget.value)
    if (this.hasDimensionTarget && this.dimensionTarget.value) params.set("dimension_1", this.dimensionTarget.value)
    if (this.hasTextileTypeTarget && this.textileTypeTarget.value) params.set("dimension_2", this.textileTypeTarget.value)

    try {
      const response = await fetch(`${this.lookupUrlValue}?${params}`, { headers: { Accept: "application/json" } })
      if (!response.ok) return

      const data = await response.json()
      if (this.hasUnitPriceTarget) this.unitPriceTarget.value = ((data.price_cents || 0) / 100).toFixed(2)
      this.#setFeedback(this.#feedbackFor(data))
      this.recalculate()
    } catch (error) {
      console.warn("[marking-form] Price lookup failed:", error)
    }
  }

  // ── Private ──────────────────────────────────────────────────

  #dimensionValue(value) {
    return parseFloat(value) || 0
  }

  #syncQuantity() {
    const allocationRows = this.allocationRowTargets.filter(row => {
      return row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value !== "1"
    })
    const linkRows = this.linkRowTargets.filter(row => {
      return row.querySelector("[data-erp--marking-form-target='linkDestroy']")?.value !== "1"
    })
    const rows = allocationRows.length > 0 ? allocationRows : linkRows

    if (rows.length === 0) {
      if (this.hasQuantityTarget) {
        this.quantityTarget.readOnly = false
        this.#setInputMode(this.quantityTarget, true)
      }
      return this.#refreshSummary()
    }

    const total = rows.reduce((sum, row) => {
      const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
        || row.querySelector("[data-erp--marking-form-target='linkQuantity']")
      return sum + (parseInt(quantity?.value) || 0)
    }, 0)

    if (allocationRows.length > 0 && this.hasTextileTypeTarget) {
      const textileTypes = [...new Set(allocationRows.map(row => row.dataset.allocationType).filter(Boolean))]
      this.textileTypeTarget.value = !this.structuredPricingValue && textileTypes.length === 1
        ? (textileTypes[0] === "white" ? "blanc" : "couleur")
        : ""
    }

    if (this.hasQuantityTarget) {
      this.quantityTarget.value = total
      this.quantityTarget.readOnly = true
      this.#setInputMode(this.quantityTarget, false)
    }

    this.#refreshSummary()
  }

  // ── Personalised pieces (R7) ─────────────────────────────────

  // The supplement follows the count: the price waits for the save
  personalizedPiecesChanged() {
    this.personalizedPiecesTarget.dataset.requested = this.personalizedPiecesTarget.value
    this.#syncPersonalizedPieces(parseInt(this.personalizedPiecesTarget.max) || 0)
    this.recalculate()
  }

  // A cleared field means no personalised piece
  // (the change event of a capped count leaves the count asked for alone)
  personalizedPiecesCommitted() {
    if (this.personalizedPiecesTarget.value !== "") return

    this.personalizedPiecesTarget.value = "0"
    this.personalizedPiecesChanged()
  }

  // At most the marked pieces: fewer marked pieces lower the personalised ones with them, more marked pieces bring
  // back the count asked for (a duplicated marking chooses its textiles after its count is copied)
  #syncPersonalizedPieces(marked) {
    if (!this.hasPersonalizedPiecesTarget) return

    const input = this.personalizedPiecesTarget
    input.dataset.requested ??= input.value
    input.max = marked
    const count = Math.min(Math.max(0, parseInt(input.dataset.requested) || 0), marked)
    if (input.value !== "" && String(count) !== input.value) {
      input.value = count
      this.#notifyFormMutation(input)
    }
    if (this.hasPersonalizedPiecesMaxTarget) {
      this.personalizedPiecesMaxTarget.textContent = this.personalizedPiecesMaxTarget.dataset.template.replace("__COUNT__", marked)
    }
    if (this.hasPersonalizedSummaryTarget) {
      this.personalizedSummaryTarget.textContent = this.personalizedSummaryTarget.dataset.template.replace("__COUNT__", count)
      this.personalizedSummaryTarget.hidden = count === 0
    }
  }

  // "+ sous-couche": a screen print with pieces on a coloured textile. A marking without size allocations keeps the
  // server's answer, given by its priced subgroups
  #refreshUnderbaseTag() {
    if (!this.hasUnderbaseTagTarget) return

    const rows = this.allocationRowTargets.filter(row => this.#markedRow(row))
    const screenPrint = this.#techniqueCode() === "screen_print"
    if (rows.length === 0 && screenPrint) return

    this.underbaseTagTarget.hidden = !screenPrint || !rows.some(row => row.dataset.allocationType === "colored")
  }

  // A size row kept on the marking with at least one piece
  #markedRow(row) {
    const destroyed = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value === "1"
    return !destroyed && (parseInt(row.querySelector("[data-erp--marking-form-target='allocationQuantity']")?.value) || 0) > 0
  }

  #refreshSummary() {
    const quantity = this.hasQuantityTarget ? (parseInt(this.quantityTarget.value) || 0) : 0
    const unitPrice = this.hasUnitPriceTarget ? (parseFloat(this.unitPriceTarget.value) || 0) : 0
    if (this.hasUnitPriceDisplayTarget) {
      this.unitPriceDisplayTarget.textContent = this.element.dataset.pricingStale === "true" ||
        this.element.dataset.markingPricingStatus === "blocked" ? t("recalculate_required") : this.#currency(unitPrice)
    }

    if (this.hasQuantitySummaryTarget) this.quantitySummaryTarget.textContent = quantity
    // The marked pieces cap the personalised ones
    this.#syncPersonalizedPieces(quantity)
    this.#refreshUnderbaseTag()
    if (!this.structuredPricingValue || this.#overridden()) this.#refreshPriceSummary(quantity, unitPrice)
    this.#refreshTextileChips()
  }

  // A manually priced marking shows its total before the save; a grid-priced one waits for the server
  #refreshPriceSummary(quantity, unitPrice) {
    const discountType = this.element.querySelector("select[name*='[discount_type]']")?.value || "percentage"
    const discount = parseInt(this.element.querySelector("select[name*='[discount_percentage]']")?.value) || 0
    const fixedDiscount = Math.round((parseFloat(this.element.querySelector("input[name*='[discount_amount]']")?.value) || 0) * 100)
    const complimentary = this.element.querySelector("input[type='checkbox'][name*='[complimentary]']")?.checked
    const unitCents = Math.round(unitPrice * 100)
    const grossCents = quantity * unitCents
    const discountedTotalCents = discountType === "fixed"
      ? Math.max(0, unitCents - fixedDiscount) * quantity
      : (discount > 0 ? Math.round(grossCents * (100 - discount) / 100) : grossCents)
    const extrasCents = complimentary ? 0 : (parseInt(this.element.dataset.markingExtrasCents) || 0)
    const total = complimentary ? 0 : (discountedTotalCents + extrasCents) / 100
    if (this.hasTotalSummaryTarget) this.totalSummaryTarget.textContent = total > 0 ? this.#currency(total) : "—"
  }

  // One "textile · pieces" chip per textile line the marking applies to (`.mk__on` of the mockup)
  #refreshTextileChips() {
    if (!this.hasLinkSummaryTarget) return

    const summary = this.linkSummaryTarget
    // Like the pricing (PriceComposer), the size allocations replace the links to whole lines as soon as there is one
    const allocationChips = this.#allocationChips()
    const labels = allocationChips.length > 0 ? allocationChips : this.#linkChips()
    const chips = labels.map(text => this.#chip(text, summary.dataset.chipClasses))
    if (chips.length === 0) chips.push(this.#chip(summary.dataset.emptyLabel, summary.dataset.emptyClasses))
    summary.replaceChildren(...chips)
  }

  #allocationChips() {
    return [ ...this.element.querySelectorAll("[data-allocation-item]") ].flatMap(section => {
      const { marked, total } = this.#allocationCounts(this.#visibleAllocationRows(section))
      if (marked === 0) return []

      const textile = section.querySelector("[data-allocation-item-title]")?.textContent?.trim() || ""
      return [ this.#chipLabel(textile, marked === total ? marked : `${marked} / ${total}`) ]
    })
  }

  #linkChips() {
    return this.linkRowTargets.flatMap(row => {
      if (row.querySelector("[data-erp--marking-form-target='linkDestroy']")?.value === "1") return []

      const textile = row.querySelector("[data-link-label]")?.textContent?.trim() || ""
      return [ this.#chipLabel(textile, row.querySelector("[data-erp--marking-form-target='linkQuantity']")?.value || "0") ]
    })
  }

  #chipLabel(textile, pieces) {
    return (this.linkSummaryTarget.dataset.chipTemplate || "__TEXTILE__ · __PIECES__")
      .replace("__TEXTILE__", textile).replace("__PIECES__", pieces)
  }

  #chip(text, classes) {
    const chip = document.createElement("span")
    chip.className = classes || ""
    chip.textContent = text
    return chip
  }

  #refreshBadges() {
    this.#refreshTechniqueBadge()
    this.#refreshUnderbaseTag()
    this.#refreshZoneBadges()
    this.#refreshActionLabels()
  }

  // A technique outside the catalogue (older marking) keeps the name the server gave it
  #refreshTechniqueBadge() {
    const technique = this.#checkedTechnique()
    if (this.hasTechniqueIconTarget && technique) this.techniqueIconTarget.className = `lni lni-${technique.dataset.icon} text-2xl leading-none`
    if (!this.hasTechniqueBadgeTarget) return
    if (technique) this.techniqueBadgeTarget.textContent = technique.dataset.label
    else if (!this.hasTechniqueTarget || !this.techniqueTarget.value) this.techniqueBadgeTarget.textContent = this.techniqueBadgeTarget.dataset.emptyLabel
  }

  #refreshZoneBadges() {
    if (this.hasPositionBadgeTarget && this.hasPositionTarget) {
      this.positionBadgeTarget.textContent = this.positionTarget.value
      this.positionBadgeTarget.hidden = !this.positionTarget.value
    }
    const custom = this.hasCustomZoneTarget && this.customZoneTarget.value.trim() !== ""
    if (this.hasCustomZoneTagTarget) this.customZoneTagTarget.hidden = !custom
    if (this.hasCustomZoneHintTarget) this.customZoneHintTarget.classList.toggle("hidden", !custom)
  }

  // The card's buttons are named after the marking ("Modifier le marquage : Sérigraphie — Dos")
  #refreshActionLabels() {
    const labels = JSON.parse(this.element.dataset.actionLabels || "{}")
    const name = [ this.hasTechniqueBadgeTarget && this.techniqueBadgeTarget.textContent.trim(),
      this.hasPositionTarget && this.positionTarget.value ].filter(Boolean).join(" — ")
    this.element.querySelectorAll("[data-action-label]").forEach(button => {
      const label = labels[button.dataset.actionLabel]
      if (label) button.setAttribute("aria-label", label.replace("__NAME__", name))
    })
  }

  #refreshDimensionsSummary() {
    if (!this.hasDimensionsSummaryTarget || !this.hasWidthTarget || !this.hasHeightTarget) return

    const width = parseFloat(this.widthTarget.value)
    const height = parseFloat(this.heightTarget.value)
    const summary = this.dimensionsSummaryTarget
    summary.textContent = (summary.dataset.template || "")
      .replace("__W__", MILLIMETRES.format(width)).replace("__H__", MILLIMETRES.format(height))
    summary.classList.toggle("hidden", !(width > 0 && height > 0))
  }

  #checkedTechnique() {
    return this.techniqueChoiceTargets.find(choice => choice.checked)
  }

  #checkedZone() {
    return this.zoneChoiceTargets.find(choice => choice.checked)
  }

  #toggleManualPriceReason() {
    if (!this.hasManualPriceReasonTarget) return

    this.manualPriceReasonTarget.classList.toggle("hidden", !this.#overridden())
  }

  #syncDiscountFields() {
    const fixed = this.element.querySelector("select[name*='[discount_type]']")?.value === "fixed"
    if (this.hasDiscountPercentageFieldTarget) this.discountPercentageFieldTarget.classList.toggle("hidden", fixed)
    if (this.hasDiscountAmountFieldTarget) this.discountAmountFieldTarget.classList.toggle("hidden", !fixed)
  }

  #syncVatSummary() {
    if (!this.hasVatSummaryTarget) return
    const vat = this.element.querySelector("select[name*='[vat_rate]']")?.selectedOptions[0]?.textContent?.trim()
    this.vatSummaryTarget.textContent = `${this.vatSummaryTarget.dataset.vatLabel} ${vat || "20%"}`
  }

  #setInputMode(input, editable) {
    input.className = editable ? input.dataset.editableClass : input.dataset.displayClass
  }

  #syncUnitPricePresentation() {
    const overridden = this.#overridden()
    if (this.hasUnitPriceDisplayTarget) this.unitPriceDisplayTarget.classList.toggle("hidden", overridden)
    if (this.hasUnitPriceCurrencyTarget) this.unitPriceCurrencyTarget.classList.toggle("hidden", !overridden)
  }

  #syncExtraAvailability() {
    const technique = this.hasTechniqueTarget ? this.techniqueTarget.value : ""
    const customerType = this.#customerType()
    let visibleCount = 0

    this.extraRowTargets.forEach(row => {
      const checkbox = row.querySelector("[data-erp--marking-form-target='extraCheckbox']")
      const matches = Boolean(technique)
        && row.dataset.extraTechnique === technique
        && row.dataset.extraCustomerType === customerType
      const selected = checkbox?.checked
      const visible = matches || selected
      row.classList.toggle("hidden", !visible)
      if (checkbox) checkbox.disabled = !visible
      row.querySelector("[data-extra-mismatch-technique]")?.classList.toggle("hidden", row.dataset.extraTechnique === technique)
      row.querySelector("[data-extra-mismatch-customer]")?.classList.toggle("hidden", row.dataset.extraCustomerType === customerType)
      if (visible) visibleCount += 1
    })

    if (this.hasExtraEmptyTarget) this.extraEmptyTarget.classList.toggle("hidden", visibleCount > 0)
  }

  #remapExtrasForCustomerType(customerType) {
    const technique = this.hasTechniqueTarget ? this.techniqueTarget.value : ""
    if (!technique) return

    this.extraRowTargets.forEach(row => {
      const checkbox = row.querySelector("[data-erp--marking-form-target='extraCheckbox']")
      if (!checkbox?.checked || row.dataset.extraTechnique !== technique || row.dataset.extraCustomerType === customerType) return

      const replacement = this.extraRowTargets.find(candidate =>
        candidate.dataset.extraTechnique === technique &&
        candidate.dataset.extraCustomerType === customerType &&
        candidate.dataset.extraCode === row.dataset.extraCode &&
        candidate.dataset.extraActive === "true"
      )
      const replacementCheckbox = replacement?.querySelector("[data-erp--marking-form-target='extraCheckbox']")
      const oldDestroy = row.querySelector("[data-erp--marking-form-target='extraDestroy']")
      const newDestroy = replacement?.querySelector("[data-erp--marking-form-target='extraDestroy']")
      if (!replacementCheckbox || !oldDestroy || !newDestroy) return

      checkbox.checked = false
      oldDestroy.value = "1"
      replacementCheckbox.checked = true
      newDestroy.value = "0"
    })
  }

  // Compatibility of each textile line with the chosen technique, zone and dimensions (step 4 of the wizard)
  #refreshCompatibility() {
    const context = {
      techniqueId: this.#checkedTechnique()?.value || "",
      zoneId: this.#checkedZone()?.value || "",
      customZone: this.hasCustomZoneTarget ? this.customZoneTarget.value.trim() : "",
      width: this.hasWidthTarget ? parseFloat(this.widthTarget.value) || 0 : 0,
      height: this.hasHeightTarget ? parseFloat(this.heightTarget.value) || 0 : 0
    }

    this.compatibilityRowTargets.forEach(row => {
      const { status, details } = this.#compatibility(row, context)
      row.dataset.compatibilityState = status
      const badge = row.querySelector("[data-compatibility-status]")
      if (badge) {
        badge.className = `inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-bold ${COMPATIBILITY_CLASSES[status]}`
        badge.textContent = t(`compatibility.status.${status}`)
      }
      const detail = row.querySelector("[data-compatibility-detail]")
      if (detail) detail.textContent = details.join(" · ")
    })
  }

  #compatibility(row, { techniqueId, zoneId, customZone, width, height }) {
    if (row.dataset.customerSupplied === "true") return { status: "approval_required", details: [ t("compatibility.customer_supplied") ] }
    if (customZone) return { status: "approval_required", details: [ t("compatibility.custom_zone") ] }
    if (!techniqueId || !zoneId) return { status: "unspecified", details: [ t("compatibility.select_definition") ] }

    const rule = this.#compatibilityRule(row, `${techniqueId}:${zoneId}`)
    if (!rule) return { status: "unspecified", details: [ t("compatibility.unspecified") ] }

    const exceeded = this.#dimensionsExceeded(rule, width, height)
    return { status: exceeded ? "incompatible" : (rule.status || "unspecified"), details: this.#ruleDetails(rule, exceeded) }
  }

  #ruleDetails(rule, exceeded) {
    const limited = rule.max_width_mm || rule.max_height_mm
    return [
      exceeded && t("compatibility.dimensions_exceeded"),
      limited && t("compatibility.max_dimensions", { width: rule.max_width_mm || "—", height: rule.max_height_mm || "—" }),
      rule.inherited && t("compatibility.inherited"),
      rule.operator_note
    ].filter(Boolean)
  }

  #dimensionsExceeded(rule, width, height) {
    const tooWide = Boolean(rule.max_width_mm) && width > Number(rule.max_width_mm)
    return tooWide || (Boolean(rule.max_height_mm) && height > Number(rule.max_height_mm))
  }

  #compatibilityRule(row, key) {
    try {
      return JSON.parse(row.dataset.compatibilityRules || "{}")[key]
    } catch {
      return null
    }
  }

  #markPricingStale() {
    this.element.dataset.pricingStale = "true"
    this.element.dataset.pricingUnavailable = "false"
    this.element.querySelectorAll("[data-pricing-status-badge]").forEach(badge => {
      badge.textContent = t("recalculate_required")
      // The card palette carries a "stale" entry, so erp--autosave clears it with the others after the save
      const palette = JSON.parse(badge.dataset.statusClasses || "{}")
      Object.values(palette).forEach(classes => badge.classList.remove(...classes.split(" ")))
      if (palette.stale) badge.classList.add(...palette.stale.split(" "))
    })
    if (this.hasUnitPriceDisplayTarget) this.unitPriceDisplayTarget.textContent = t("recalculate_required")
    if (this.hasTotalSummaryTarget) this.totalSummaryTarget.textContent = "—"
    this.element.querySelector("[data-pricing-stale]")?.classList.remove("hidden")
    this.element.querySelector("[data-pricing-breakdown]")?.classList.add("opacity-50")
    this.dispatch("changed")
  }

  #feedbackFor(data) {
    if (data.price_cents != null && !data.quote_required) return null

    switch (data.reason) {
      case "below_minimum":
        return { level: "warning", message: t("below_minimum", { count: data.minimum_quantity }) }
      case "above_maximum":
        return { level: "warning", message: t("above_maximum", { count: data.maximum_quantity }) }
      case "missing_dimensions":
        return { level: "info", message: t("missing_dimensions") }
      case "missing_tier":
        return { level: "warning", message: t("missing_tier") }
      case "ambiguous_grid":
        return { level: "warning", message: t("ambiguous_grid") }
      case "invalid_price":
        return { level: "warning", message: t("invalid_price") }
      case "no_grid":
        return { level: "info", message: t("no_grid") }
      default:
        return data.quote_required ? { level: "warning", message: t("quote_required") } : null
    }
  }

  #setFeedback(feedback) {
    if (!this.hasPriceFeedbackTarget) return

    const element = this.priceFeedbackTarget
    if (!feedback) {
      element.textContent = ""
      element.classList.add("hidden")
      return
    }

    element.textContent = feedback.message
    element.className = `mt-1 text-xs ${feedback.level === "warning" ? "text-amber-700" : "text-black/60"}`
  }

  #overridden() {
    return this.hasPriceOverrideTarget && this.priceOverrideTarget.checked
  }

  #customerType() {
    return this.customerTypeValue || "direct"
  }

  #currency(amount) {
    return formatCurrency(amount)
  }
}
